// Architectural summary: Pure formatting utilities for the receipt print preview.
// Used by ReceiptPrintPreviewModal to format timestamps and derive display-safe
// order numbers from order codes. No side effects, no external dependencies.
// Changes here affect print-preview metadata display accuracy.
import { roundMoney } from '../../../../../utils/money';

const pad = (n: number) => String(n).padStart(2, '0');

// DD/MM/YYYY HH:MM:SS — used for order start and closing timestamps
export const formatOrderDate = (dateStr?: string): string => {
  const date = dateStr ? new Date(dateStr) : new Date();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const yyyy = date.getFullYear();
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
};

// DD/MM/YYYY HH:MM:SS — used for the print timestamp
export const formatPrintDate = (): string => {
  const date = new Date();
  const dd = pad(date.getDate());
  const mm = pad(date.getMonth() + 1);
  const yyyy = date.getFullYear();
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
};

// Strips the tenant-name prefix from order codes of the form Tenant/seq/id.
// Returns the numeric id portion for single-part codes, or the full code as fallback.
export const getOrderNumberDisplay = (orderCode?: string, orderId?: number): string => {
  if (!orderCode) return String(orderId || '');
  const parts = orderCode.split('/');
  if (parts.length > 2) {
    return parts.slice(1).join('/');
  }
  return orderCode;
};

const MAX_QTY_PER_ROW = 99;

export const getReceiptDiscountTotal = (
  lines: { unitPrice: number; quantity: number; discountInput?: number | null }[],
  discount: number,
  roundAmounts = false,
): number => lines.reduce((sum, line) => sum + roundMoney(line.unitPrice * line.quantity, roundAmounts), 0)
  - lines.reduce((sum, line) => sum + roundMoney(line.unitPrice * line.quantity - (line.discountInput ?? 0), roundAmounts), 0)
  + discount;

export const getReceiptGroupKey = (line: {
  id: number; productId: number | null; unitPrice: number; quantity: number; discountInput?: number | null;
}): string => (
  line.productId == null ? `general_${line.id}` : `${line.productId}_${line.unitPrice}_${line.quantity > 0 ? (line.discountInput ?? 0) / line.quantity : 0}`
);

export const groupOrderlines = <T extends { id: number; productId: number | null; unitPrice: number; quantity: number; discountInput?: number | null; roundAmounts?: boolean; billableAmount?: number }>(
  lines: T[],
): (T & { billableAmount: number })[] => {
  const totals: Record<string, { base: T; quantity: number; discountInput: number; billableAmount: number }> = {};

  for (const line of lines) {
    const key = getReceiptGroupKey(line);
    const billableAmount = line.billableAmount ?? roundMoney(line.unitPrice * line.quantity - (line.discountInput ?? 0), line.roundAmounts ?? false);
    if (totals[key]) {
      totals[key].quantity += line.quantity;
      totals[key].discountInput += line.discountInput ?? 0;
      totals[key].billableAmount += billableAmount;
    } else {
      totals[key] = { base: line, quantity: line.quantity, discountInput: line.discountInput ?? 0, billableAmount };
    }
  }

  const result: (T & { billableAmount: number })[] = [];
  for (const { base, quantity, discountInput, billableAmount } of Object.values(totals)) {
    const mergedDiscount = discountInput > 0 ? discountInput : null;
    let remaining = quantity;
    let remainingAmount = billableAmount;
    while (remaining > 0) {
      const chunk = Math.min(remaining, MAX_QTY_PER_ROW);
      const chunkAmount = chunk === remaining ? remainingAmount : roundMoney(billableAmount * chunk / quantity, base.roundAmounts ?? false);
      result.push({ ...base, quantity: chunk, discountInput: mergedDiscount, billableAmount: chunkAmount });
      remainingAmount -= chunkAmount;
      remaining -= chunk;
    }
  }
  return result;
};

const RECEIPT_FONT_LINK_ID = 'receipt-font';

// Loads Noto Sans Hebrew from Google Fonts before html2canvas capture so Hebrew
// glyphs render correctly. Keeps the <link> in the DOM - second call is a no-op.
export async function loadReceiptFont(): Promise<void> {
  const linkId = RECEIPT_FONT_LINK_ID;
  let link = document.getElementById(linkId) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@400;500;600;700;800&display=swap';
    await new Promise<void>((res) => {
      link!.addEventListener('load', () => res(), { once: true });
      link!.addEventListener('error', () => res(), { once: true });
      document.head.appendChild(link!);
    });
  }
  await Promise.all([
    document.fonts.load('400 1em "Noto Sans Hebrew"'),
    document.fonts.load('500 1em "Noto Sans Hebrew"'),
    document.fonts.load('700 1em "Noto Sans Hebrew"'),
    document.fonts.load('800 1em "Noto Sans Hebrew"'),
  ]);
}

// Captures the visible receipt DOM node into a high-quality JPEG data URL.
// Returns undefined in test environments or on error.
export async function captureReceiptSnapshot(
  receiptPaper: HTMLDivElement | null,
): Promise<string | undefined> {
  if (!receiptPaper) return undefined;
  if (process.env.NODE_ENV === 'test') return undefined;
  try {
    await loadReceiptFont();
    const module = await import('html2canvas');
    const html2canvas = module.default;
    const canvas = await html2canvas(receiptPaper, {
      backgroundColor: '#ffffff',
      scale: 3,
      useCORS: true,
      logging: false,
      imageTimeout: 0,
    });
    return canvas.toDataURL('image/jpeg', 0.98);
  } catch {
    return undefined;
  }
}
